import { BRAND } from "@/lib/brand";
import { periodLabel, shiftISO, shiftMonthISO, todayISO } from "@/lib/dateUtils";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PERIODS = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "custom", label: "Custom" },
];

const DATE_INPUT_CLASS =
  "text-sm px-2.5 py-1.5 rounded-md border border-slate-200 bg-white " +
  "text-slate-800 focus:outline-none focus:ring-2";

export default function PeriodNavigator({
  period,
  setPeriod,
  refDate,
  setRefDate,
  customStart,
  setCustomStart,
  customEnd,
  setCustomEnd,
}) {
  const shift = (dir) => {
    if (period === "day") setRefDate(shiftISO(refDate, dir));
    else if (period === "week") setRefDate(shiftISO(refDate, dir * 7));
    else if (period === "month") setRefDate(shiftMonthISO(refDate, dir));
  };

  const jumpToday = () => setRefDate(todayISO());

  // Surface a reversed range inline rather than firing a request that 400s.
  const rangeInvalid =
    period === "custom" &&
    !!customStart &&
    !!customEnd &&
    customStart > customEnd;

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 px-3 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      data-testid="period-navigator"
    >
      <div
        className="flex items-center bg-slate-100 rounded-lg p-0.5"
        role="tablist"
      >
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            role="tab"
            aria-selected={period === p.key}
            data-testid={`period-tab-${p.key}`}
            className={`px-3.5 py-1.5 text-sm rounded-md transition font-medium ${
              period === p.key
                ? "bg-white shadow-sm text-slate-900"
                : "text-slate-500 hover:text-slate-800"
            }`}
            style={period === p.key ? { color: BRAND.teal } : undefined}
          >
            {p.label}
          </button>
        ))}
      </div>

      {period === "custom" ? (
        <div
          className="flex flex-col sm:flex-row sm:items-center gap-2"
          data-testid="period-custom-range"
        >
          <div className="flex items-center gap-2">
            <input
              type="date"
              aria-label="Start date"
              value={customStart || ""}
              max={customEnd || undefined}
              onChange={(e) => setCustomStart(e.target.value)}
              data-testid="period-custom-start"
              className={DATE_INPUT_CLASS}
              style={{ "--tw-ring-color": BRAND.teal }}
            />
            <span className="text-sm text-slate-400">to</span>
            <input
              type="date"
              aria-label="End date"
              value={customEnd || ""}
              min={customStart || undefined}
              onChange={(e) => setCustomEnd(e.target.value)}
              data-testid="period-custom-end"
              className={DATE_INPUT_CLASS}
              style={{ "--tw-ring-color": BRAND.teal }}
            />
          </div>
          {rangeInvalid && (
            <span
              className="text-xs font-medium text-red-600"
              data-testid="period-custom-error"
            >
              End date must be on or after the start date.
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => shift(-1)}
            data-testid="period-prev"
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
            title="Previous"
          >
            <ChevronLeft size={18} />
          </button>
          <div
            className="text-sm font-semibold text-slate-800 text-center min-w-[180px]"
            data-testid="period-label"
          >
            {periodLabel(period, refDate)}
          </div>
          <button
            onClick={() => shift(1)}
            data-testid="period-next"
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
            title="Next"
          >
            <ChevronRight size={18} />
          </button>
          <button
            onClick={jumpToday}
            data-testid="period-today"
            className="ml-1 text-xs font-semibold px-2.5 py-1 rounded-md text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 transition"
          >
            Today
          </button>
        </div>
      )}
    </div>
  );
}
